import { useCheckBookingAvailabilityMutation, useGetApartmentMutation, useGetBookingMutation, usePostBookingMutation, usePutBookingMutation } from '@services/member/apartment/apartment.endpoints';
import { Apartment } from '@services/member/apartment/apartment.types';
import { Booking, BookingToCreate } from '@services/member/booking/booking.types';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDebounceCallback } from 'usehooks-ts';

export const useAddPageVM = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id, bookingId } = useParams();
    // Vue d'origine (liste des réservations, planning) transmise via l'état de navigation ;
    // à défaut on revient sur l'onglet Réservations de la fiche logement comme avant.
    const backTo = (location.state as { backTo?: string } | null)?.backTo ?? `/members-management/apartments/${id}/details/bookings`;
    const [apartment, setApartment] = useState<Apartment>();
    const [booking, setBooking] = useState<Booking>();
    const [isEdit, setIsEdit] = useState(false);
    const [isAvailable, setIsAvailable] = useState(true);
    const [bookingModalOpen, setBookingModalOpen] = useState(false);

    const [
        getApartment,
        {
            isLoading: isGetApartmentLoading,
            isUninitialized: isGetApartmentUninitialized,
            isError: isGetApartmentError,
            isSuccess: isGetApartmentSuccess,
            error: getApartmentError,
            data: getApartmentResponse
        }
    ] = useGetApartmentMutation();

    const [
        checkBookingAvailability,
        {
            isLoading: isCheckBookingAvailabilityLoading,
            data: checkBookingAvailabilityResponse
        }
    ] = useCheckBookingAvailabilityMutation();

    const [
        getBooking,
        {
            isLoading: isGetBookingLoading,
            isError: isGetBookingError,
            error: getBookingError,
            isSuccess: isGetBookingSuccess,
            data: getBookingResponse
        }
    ] = useGetBookingMutation();

    const [
        createBooking,
        {
            isLoading: isCreateBookingLoading,
            isError: isCreateBookingError,
            isSuccess: isCreateBookingSuccess,
            error: createBookingError,
            data: createBookingResponse
        }
    ] = usePostBookingMutation();

    const [
        updateBooking,
        {
            isLoading: isUpdateBookingLoading,
            isError: isUpdateBookingError,
            error: updateBookingError,
            isSuccess: isUpdateBookingSuccess,
            data: updateBookingResponse
        }
    ] = usePutBookingMutation();


    useEffect(() => {
        if (!id) return;
        getApartment({ id });
    }, [id]);

    useEffect(() => {
        if (!bookingId || !id) return;
        getBooking({ id, bookingId });
        if (bookingId) {
            setIsEdit(true);
        }
    }, [id, bookingId]);

    useEffect(() => {
        if (isCheckBookingAvailabilityLoading) return;
        if (checkBookingAvailabilityResponse) {
            setIsAvailable(checkBookingAvailabilityResponse.data);
        }
    }, [isCheckBookingAvailabilityLoading, checkBookingAvailabilityResponse]);


    useEffect(() => {
        if (isGetApartmentLoading) return;
        if (isGetApartmentError) return;
        if (getApartmentError) return;

        if (isGetApartmentSuccess && getApartmentResponse) {
            setApartment(getApartmentResponse.data);
        }
    }, [isGetApartmentError, isGetApartmentSuccess, getApartmentError, getApartmentResponse, isGetApartmentLoading]);

    useEffect(() => {
        if (isGetBookingLoading) return;
        if (isGetBookingError) return;
        if (getBookingError) return;

        if (isGetBookingSuccess && getBookingResponse) {
            setBooking(getBookingResponse.data);
        }
    }, [isGetBookingError, isGetBookingSuccess, getBookingError, getBookingResponse, isGetBookingLoading]);

    useEffect(() => {
        if (isUpdateBookingLoading) return;
        if (isUpdateBookingError) return;
        if (updateBookingError) return;

        if (isUpdateBookingSuccess && updateBookingResponse) {
            navigate(backTo);
        }
    }, [isUpdateBookingError, isUpdateBookingSuccess, updateBookingError, updateBookingResponse, isUpdateBookingLoading, backTo, navigate]);

    useEffect(() => {
        if (isCreateBookingLoading) return;
        if (isCreateBookingError) return;
        if (createBookingError) return;

        if (isCreateBookingSuccess && createBookingResponse) {
            navigate(backTo);
        }
    }, [isCreateBookingError, isCreateBookingSuccess, createBookingError, createBookingResponse, isCreateBookingLoading, backTo, navigate]);


    const submitForm = async (values: BookingToCreate) => {
        if (!id) return;
        if (bookingId) {
            await updateBooking({ id: id, bookingId: bookingId, booking: values });
        } else {
            await createBooking({ id: id, booking: values });
        }
    }

    const checkAvailability = (values: BookingToCreate) => {
        if (!id || !values.arrivalDate || !values.departureDate) return;
        checkBookingAvailability({ id: id, start: values.arrivalDate, end: values.departureDate, sharedFirstDay: values.sharedFirstDay == "" ? false : values.sharedFirstDay, sharedLastDay: values.sharedLastDay == "" ? false : values.sharedLastDay, ...(bookingId ? { bookingId } : {}) });
    }

    const debouncedCheckAvailability = useDebounceCallback(checkAvailability, 500)

    return {
        apartmentIsLoading: isGetApartmentUninitialized || isGetApartmentLoading,
        isError: isGetApartmentError && !apartment,
        apartment,
        isLoading: isCreateBookingLoading || isGetBookingLoading || isUpdateBookingLoading,
        isEdit,
        booking,
        submitForm,
        checkAvailability: debouncedCheckAvailability,
        isAvailable,
        apartmentId: id,
        bookingModalOpen,
        setBookingModalOpen
    };
};
